import React, { useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useRouter } from "expo-router";
import {
  Entypo,
  Foundation,
  Ionicons,
  MaterialCommunityIcons,
} from "@expo/vector-icons";

import FilterModal from "@/screens/Explore/ExploreCategory/Components/Filter/FilterModal";
import { type UserFilterCriteria } from "@/schemas/profile/filters/UserFilterCriteria";
import { RecommendationCategory } from "@/schemas/user/explore";
import BackButton from "@/Components/Headers/Components/BackButton";
import { themes } from "@/Components/Styling/themeConfigs";
import {
  EXPLORE_CATEGORY_ICONS,
  IconConfig,
  getCategoryDisplayName,
  shouldShowCategoryFilter,
} from "@/utils/explore";
import { Spacings } from "@/styling/spacings";
import { formatNumberForDisplay } from "@/formatting/number";
import { countActiveFilters } from "@/utils/filters";
import FilterIcon from "@/Components/Icons/FilterIcon";

type Props = {
  category: RecommendationCategory;
  profileCount: number;
  hideFilterIcon?: boolean;
};

// TODO: this is copied from notification screen header, create a component?
const ExploreByCategoryScreenHeader = ({
  category,
  profileCount,
  hideFilterIcon,
}: Props): React.JSX.Element => {
  const router = useRouter();
  const currentTheme = themes.morning;
  const iconConfig: IconConfig = EXPLORE_CATEGORY_ICONS[category];
  const iconSize =
    currentTheme.typography.h1.size.mobile * (iconConfig.sizeMultiplier || 1);

  const customOnPress = (): void => {
    router.canGoBack() ? router.back() : router.replace("/(main)/explore/home");
  };

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [filters, setFilters] = useState<UserFilterCriteria | null>(null); // TODO: store in context like Nearby?
  const [filterCount, setFilterCount] = useState<number>(0);

  function handleApplyFilters(filters: UserFilterCriteria): void {
    setFilters(filters);
    setIsFilterModalVisible(false);
  }

  useEffect(() => {
    // TODO: reset and fetch profiles based on new filters
    const activeFiltersCount = countActiveFilters(filters);
    setFilterCount(activeFiltersCount);
  }, [filters]);

  return (
    <View style={styles.container}>
      {/* left side*/}
      <View style={styles.leftContainer}>
        <BackButton onPress={customOnPress} />
        {/* icon, copied from CategorySectionHeader, pending refactoring after switching to backend supplied icon */}
        <View style={styles.iconContainer}>
          {iconConfig.iconFamily === "Ionicons" && (
            <Ionicons
              name={iconConfig.name as keyof typeof Ionicons.glyphMap}
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
          {iconConfig.iconFamily === "MaterialCommunityIcons" && (
            <MaterialCommunityIcons
              name={
                iconConfig.name as keyof typeof MaterialCommunityIcons.glyphMap
              }
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
          {iconConfig.iconFamily === "Entypo" && ( // eslint-disable-line @cspell/spellchecker
            <Entypo
              name={iconConfig.name as keyof typeof Entypo.glyphMap}
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
          {iconConfig.iconFamily === "Foundation" && ( // eslint-disable-line @cspell/spellchecker
            <Foundation
              name={iconConfig.name as keyof typeof Foundation.glyphMap}
              size={iconSize}
              color={"black"}
              {...(iconConfig.style ? { style: iconConfig.style } : {})}
            />
          )}
        </View>
        {/* title text */}
        <View style={styles.titleTextContainer}>
          <Text style={[styles.titleText, styles.bold]}>
            {getCategoryDisplayName(category)}
          </Text>
          {profileCount > 0 && (
            <Text style={styles.profileCountText}>
              {formatNumberForDisplay(profileCount)} profiles
            </Text>
          )}
        </View>
      </View>
      {!hideFilterIcon && shouldShowCategoryFilter(category) && (
        <>
          {/* right side */}
          <FilterIcon
            filterCount={filterCount}
            onPress={() => setIsFilterModalVisible(true)}
          />

          <FilterModal
            isFilterModalVisible={isFilterModalVisible}
            onApplyFilters={handleApplyFilters}
            onClose={() => setIsFilterModalVisible(false)}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingHorizontal: Spacings.s2,
    paddingTop: Spacings.s1,
    marginBottom: Spacings.s2,
  },
  leftContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacings.s2,
  },
  titleTextContainer: {
    flexDirection: "column",
  },
  bold: {
    fontWeight: "bold",
  },
  titleText: {
    fontSize: 20,
  },
  profileCountText: {
    fontSize: themes.morning.typography.body.size.mobile,
    color: "gray",
  },
  iconContainer: {
    width: 48,
    height: 48,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 30,
    padding: Spacings.s3,
    marginRight: Spacings.s2,
    backgroundColor: "#fee140",
  },
});

export default ExploreByCategoryScreenHeader;
